import { apiClient } from "@/apps/credit-line/services/apiClient/apiClient";
import { creditLineApiUrls as apiUrl } from "@/apps/credit-line/services/apiClient/apiUrl";
import { getPathNameFromUrl as urlPathName } from "@/apps/credit-line/services/apiClient/apiUrl";
import { jsonHeader } from "@/apps/credit-line/services/apiClient/apiHeader";
import { jsonHeader as coreAppJsonHeader } from "@/apps/core-app-onboarding/services/apiClient/apiHeader";
import { CreditLineService } from "./creditline.service";
import { AxiosResponse } from "axios";
import { ICreditLineDetails } from "@/apps/credit-line/models/CreditLineDetails";
import { IOffers, IApplicationStatus } from "@/apps/credit-line/models/IOffer";
import { IBaseResponse } from "@/apps/credit-line/models/IBaseResponse";
import {
  IWithdrawalAvailable,
  IWithdrawalEstimate,
} from "@/apps/credit-line/models/IWithdrawalEstimate";
import { IWithdrawalSecure } from "@/apps/credit-line/models/IWithdrawalSecure";
import { IPaymentData } from "@/apps/credit-line/models/IPaymentData";
import { IShippingCreditCard } from "@/apps/credit-line/models/IShippingCreditCard";
import { IDashboardTransaction } from "@/apps/credit-line/models/IDashboardTransaction";
import { IValidateCoveredZone } from "@/apps/credit-line/models/IValidateCoveredZone";
import { IChangeShipmentsStatus } from "@/apps/credit-line/models/IChangeShipmentsStatus";
import {
  CardDetailsRequest,
  CcPinChangeRequest,
  CcStatusToggleRequest,
  CcDeleteVirtualRequest,
  CcCreateVirtualRequest,
  IBankAccountVerificationRequest,
  WithdrawalAvailableRequest,
  IShippingCreditCardRequest,
  DashboardTransactionDetailsRequest,
  ChangeShipmentStatusRequest,
  IUnlinkBankAccountRequest,
  IValidateShippingCoverageZoneRequest,
  NationalIdValidationRequest,
  IRequireAmlSurveyRequest,
  ISaveAmlSurveyRequest,
  ILogEventRequest,
  IFrontendLogMessageRequest,
  BaseRequest,
  ReportNotReceivedCardRequest,
  SummaryRequest,
  InStoreOfferRequest,
  InStoreAcceptOfferRequest,
  CreateCardUserRequest,
  CardPinRequest,
  ICreditCheckPermissionRequest,
  IAcceptSignatureRequest,
  IReportCardTransactionRequest,
  IFetchCardTransactionReportRequest,
  LatestCardShipmentRequest,
  IFetchConsumerSettingsRequest,
  IFetchShipmentLogs,
  HandleKycFlowRequest,
  ContractDetailsRequest,
  AcceptLimitIncreaseRequest,
  FetchAvailableDocumentsRequest,
} from "@/apps/credit-line/types/requests";
import {
  IAccountStatements,
  IStatementsPeriodsContainer,
} from "@/apps/credit-line/models/IAccountStatement";
import { IBankOptionInfo } from "@/apps/credit-line/models/IBankOptionInfo";
import {
  CcActivationResponse,
  CcDetailsResponse,
  CcPingChangeResponse,
  CcStatusToggleResponse,
  CcDeleteVirtualResponse,
  CcCreateVirtualResponse,
  OptOutSurveyResponse,
  ReportCardNotDeliveredResponse,
  SunshineJwtResponse,
  MasterCardBenefitsResponse,
  InStoreOfferResponse,
  CreditLineCountryResponse,
  CreateCardUserResponse,
  CcPinResponse,
  CreditCheckPermissionResponse,
  AcceptSignatureResponse,
  ReportCardTransactionResponse,
  FetchTransactionReportResponse,
  TriggerCredolabDownloadResponse,
  SettingsResponse,
  LimitIncreaseOfferResponse,
  ContractDetailsResponse,
  FetchAvailableDocumentsResponse,
} from "@/apps/credit-line/types/responses";
import { ShipmentLogsResponse } from "@/apps/credit-line/types/ShipmentLogsResponse";
import {
  NationalIdValidationResponse,
  AreValidationsRequiredResponse,
} from "@/apps/credit-line/models/IKycValidations";
import {
  RequireAmlSurveyResponse,
  SaveAmlSurveyResponse,
} from "@/apps/credit-line/models/IAmlSurvey";
import { LatestShipmentAddressModel } from "../components/CreditCard/ReportCard/types";
import { ActivateCartAddOnRequest } from "@/apps/credit-line/types/requests";
import { IAddOnDetailsResponse, IActivateCartAddOnResponse } from "@/apps/credit-line/types/responses";
import {
  DisableCardsRequest,
  IDisableCardsResponse,
} from "@/apps/dashboard/services/credit-card/bulk-disable-cards.service";
import {
  GetCountryShipmentProviderConfigurationsRequest,
  UpdateCountryShipmentProviderConfigurationsRequest,
} from "@/apps/credit-line/types/requests";
import {
  GetCountryShipmentProviderConfigurationsResponse,
  UpdateCountryShipmentProviderConfigurationsResponse,
} from "@/apps/credit-line/types/responses";
import { IShippingCoverageResponse } from "@/apps/credit-line/models/IShippingCoverageResponse";

export const HTTP_METHOD_GET = "GET";
export const HTTP_METHOD_POST = "POST";

type NetworkResponse<T> = Promise<AxiosResponse<T>>;

export class Service implements CreditLineService {
  getCountry(params: BaseRequest): NetworkResponse<CreditLineCountryResponse> {
    return apiClient.post(
      apiUrl.GET_COUNTRY,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.GET_COUNTRY),
        JSON.stringify(params)
      )
    );
  }

  getCreditLineSummary(
    params: SummaryRequest
  ): NetworkResponse<ICreditLineDetails> {
    return apiClient.post(
      apiUrl.CREDIT_LINE_SUMMARY,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.CREDIT_LINE_SUMMARY),
        JSON.stringify(params)
      )
    );
  }
  getOffers(params: unknown): NetworkResponse<IOffers> {
    return apiClient.post(
      apiUrl.GET_OFFER,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.GET_OFFER),
        JSON.stringify(params)
      )
    );
  }
  acceptOffer(params: unknown): NetworkResponse<IBaseResponse> {
    return apiClient.post(
      apiUrl.ACCEPT_OFFER,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.ACCEPT_OFFER),
        JSON.stringify(params)
      )
    );
  }
  getCreditLineContractsDetails(
    params: ContractDetailsRequest
  ): NetworkResponse<ContractDetailsResponse> {
    return apiClient.post(
      apiUrl.CONTRACT_DETAILS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.CONTRACT_DETAILS),
        JSON.stringify(params)
      )
    );
  }
  fetchAvailableDocuments(
    params: FetchAvailableDocumentsRequest
  ): NetworkResponse<FetchAvailableDocumentsResponse> {
    return apiClient.post(
      apiUrl.FETCH_AVAILABLE_DOCUMENTS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.FETCH_AVAILABLE_DOCUMENTS),
        JSON.stringify(params)
      )
    );
  }
  closeCreditLine(params: unknown): NetworkResponse<IBaseResponse> {
    return apiClient.post(
      apiUrl.CLOSE_CREDITLINE,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.CLOSE_CREDITLINE),
        JSON.stringify(params)
      )
    );
  }
  getWithdrawalEstimate(params: unknown): NetworkResponse<IWithdrawalEstimate> {
    return apiClient.post(
      apiUrl.WITHDRAWAL_ESTIMATE,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.WITHDRAWAL_ESTIMATE),
        JSON.stringify(params)
      )
    );
  }
  getWithdrawalAvailable(
    params: WithdrawalAvailableRequest
  ): NetworkResponse<IWithdrawalAvailable> {
    return apiClient.post(
      apiUrl.WITHDRAWAL_AVAILABLE,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.WITHDRAWAL_AVAILABLE),
        JSON.stringify(params)
      )
    );
  }

  logEvent(params: ILogEventRequest): Promise<AxiosResponse> {
    return apiClient.post(
      apiUrl.LOG_EVENT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.LOG_EVENT),
        JSON.stringify(params)
      )
    );
  }

  secureWithdrawal(params: unknown): NetworkResponse<IWithdrawalSecure> {
    return apiClient.post(
      apiUrl.SECURE_WITHDRAWAL,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.SECURE_WITHDRAWAL),
        JSON.stringify(params)
      )
    );
  }
  requestPayment(params: unknown): NetworkResponse<IPaymentData> {
    return apiClient.post(
      apiUrl.REQUEST_PAYMENT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.REQUEST_PAYMENT),
        JSON.stringify(params)
      )
    );
  }
  getApplicationStatus(params: unknown): NetworkResponse<IApplicationStatus> {
    return apiClient.post(
      apiUrl.APPLICATION_STATUS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.APPLICATION_STATUS),
        JSON.stringify(params)
      )
    );
  }
  getStatementsPeriods(
    params: unknown
  ): NetworkResponse<IStatementsPeriodsContainer> {
    return apiClient.post(
      apiUrl.STATEMENTS_PERIODS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.STATEMENTS_PERIODS),
        JSON.stringify(params)
      )
    );
  }
  getStatementDetails(params: unknown): NetworkResponse<IAccountStatements> {
    return apiClient.post(
      apiUrl.STATEMENT_DETAILS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.STATEMENT_DETAILS),
        JSON.stringify(params)
      )
    );
  }
  optOut(params: unknown): NetworkResponse<IBaseResponse> {
    return apiClient.post(
      apiUrl.OPT_OUT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.OPT_OUT),
        JSON.stringify(params)
      )
    );
  }
  checkOptOutSurvey(params: unknown): NetworkResponse<OptOutSurveyResponse> {
    return apiClient.post(
      apiUrl.OPT_OUT_CHECK_SURVEY,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.OPT_OUT_CHECK_SURVEY),
        JSON.stringify(params)
      )
    );
  }
  saveOptOutSurvey(params: unknown): NetworkResponse<IBaseResponse> {
    return apiClient.post(
      apiUrl.OPT_OUT_SAVE_SURVEY,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.OPT_OUT_SAVE_SURVEY),
        JSON.stringify(params)
      )
    );
  }
  getAllBanks(): NetworkResponse<IBankOptionInfo[]> {
    return apiClient.get(
      apiUrl.GET_ONLINE_TRANSFER_BANKS,
      jsonHeader(HTTP_METHOD_GET, urlPathName(apiUrl.GET_ONLINE_TRANSFER_BANKS))
    );
  }
  validateBankAccount(
    params: IBankAccountVerificationRequest
  ): NetworkResponse<IBaseResponse> {
    return apiClient.post(
      apiUrl.VALIDATE_BANK_ACCOUNT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.VALIDATE_BANK_ACCOUNT),
        JSON.stringify(params)
      )
    );
  }
  unlinkBankAccount(
    params: IUnlinkBankAccountRequest
  ): NetworkResponse<IBaseResponse> {
    return apiClient.post(
      apiUrl.UNLINK_BANK_ACCOUNT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.UNLINK_BANK_ACCOUNT),
        JSON.stringify(params)
      )
    );
  }
  activateCreditCard(params: unknown): NetworkResponse<CcActivationResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_ACTIVATION,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_ACTIVATION),
        JSON.stringify(params)
      )
    );
  }
  getCardDetails(
    params: CardDetailsRequest
  ): NetworkResponse<CcDetailsResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_DETAILS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_DETAILS),
        JSON.stringify(params)
      )
    );
  }
  getCreditCardPinNumber(
    params: CardPinRequest
  ): NetworkResponse<CcPinResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_PIN,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_PIN),
        JSON.stringify(params)
      )
    );
  }
  shipCreditCard(
    params: IShippingCreditCardRequest
  ): NetworkResponse<IShippingCreditCard> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_SHIPPING,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_SHIPPING),
        JSON.stringify(params)
      )
    );
  }
  getLatestShipment(
    body: LatestCardShipmentRequest
  ): NetworkResponse<LatestShipmentAddressModel> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_GET_LATEST_SHIPMENT,
      body,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_GET_LATEST_SHIPMENT),
        JSON.stringify(body)
      )
    );
  }
  getDashboardTransactions(
    params: DashboardTransactionDetailsRequest
  ): NetworkResponse<IDashboardTransaction> {
    return apiClient.post(
      apiUrl.PAYJOY_DASHBOARD_TRANSACTIONS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_DASHBOARD_TRANSACTIONS),
        JSON.stringify(params)
      )
    );
  }
  changeShipmentStatus(
    params: ChangeShipmentStatusRequest
  ): NetworkResponse<IChangeShipmentsStatus> {
    return apiClient.post(
      apiUrl.PAYJOY_DASHBOARD_UPDATE_SHIPMENT_STATUS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_DASHBOARD_UPDATE_SHIPMENT_STATUS),
        JSON.stringify(params)
      )
    );
  }
  validateCoverageZone(
    params: IValidateShippingCoverageZoneRequest
  ): NetworkResponse<IValidateCoveredZone> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_VALIDATE_COVERAGE_ZONE,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_VALIDATE_COVERAGE_ZONE),
        JSON.stringify(params)
      )
    );
  }
  updateCcPin(
    params: CcPinChangeRequest
  ): NetworkResponse<CcPingChangeResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_UPDATE_PIN,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_UPDATE_PIN),
        JSON.stringify(params)
      )
    );
  }
  toggleCcStatus(
    params: CcStatusToggleRequest
  ): NetworkResponse<CcStatusToggleResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_TOGGLE_STATUS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_TOGGLE_STATUS),
        JSON.stringify(params)
      )
    );
  }
  deleteVirtualCreditCard(
    params: CcDeleteVirtualRequest
  ): NetworkResponse<CcDeleteVirtualResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_DELETE_VIRTUAL,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_DELETE_VIRTUAL),
        JSON.stringify(params)
      )
    );
  }
  createVirtualCreditCard(
    params: CcCreateVirtualRequest
  ): NetworkResponse<CcCreateVirtualResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_CREATE_VIRTUAL,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_CREATE_VIRTUAL),
        JSON.stringify(params)
      )
    );
  }
  areValidationRequired(
    params: HandleKycFlowRequest
  ): NetworkResponse<AreValidationsRequiredResponse> {
    return apiClient.post(
      apiUrl.VALIDATIONS_ARE_REQUIRED,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.VALIDATIONS_ARE_REQUIRED),
        JSON.stringify(params)
      )
    );
  }
  nationalIdValidation(
    params: NationalIdValidationRequest
  ): NetworkResponse<NationalIdValidationResponse> {
    return apiClient.post(
      apiUrl.NATIONAL_ID_VALIDATION,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.NATIONAL_ID_VALIDATION),
        JSON.stringify(params)
      )
    );
  }
  RequireAmlSurvey(
    params: IRequireAmlSurveyRequest
  ): NetworkResponse<RequireAmlSurveyResponse> {
    return apiClient.post(
      apiUrl.REQUIRE_AML_SURVEY,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.REQUIRE_AML_SURVEY),
        JSON.stringify(params)
      )
    );
  }
  SaveAmlSurvey(
    params: ISaveAmlSurveyRequest
  ): NetworkResponse<SaveAmlSurveyResponse> {
    return apiClient.post(
      apiUrl.SAVE_AML_SURVEY,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.SAVE_AML_SURVEY),
        JSON.stringify(params)
      )
    );
  }
  GetSunshineJwt(params: BaseRequest): NetworkResponse<SunshineJwtResponse> {
    return apiClient.post(
      apiUrl.CREATE_SUNSHINE_JWT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.CREATE_SUNSHINE_JWT),
        JSON.stringify(params)
      )
    );
  }
  reportNotReceivedCard(
    params: ReportNotReceivedCardRequest
  ): NetworkResponse<ReportCardNotDeliveredResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_REPORT_NOT_RECEIVED_CARD,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_REPORT_NOT_RECEIVED_CARD),
        JSON.stringify(params)
      )
    );
  }
  increaseCreditLimit(
    params: AcceptLimitIncreaseRequest
  ): NetworkResponse<LimitIncreaseOfferResponse> {
    return apiClient.post(
      apiUrl.INCREASE_CREDIT_LIMIT_AMOUNT,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.INCREASE_CREDIT_LIMIT_AMOUNT),
        JSON.stringify(params)
      )
    );
  }

  async getMastercardBenefits(
    params: BaseRequest
  ): NetworkResponse<MasterCardBenefitsResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_MASTERCARD_BENEFITS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_MASTERCARD_BENEFITS),
        JSON.stringify(params)
      )
    );
  }

  getCreditLineInStoreOffer(
    params: InStoreOfferRequest
  ): NetworkResponse<InStoreOfferResponse> {
    return apiClient.post(
      apiUrl.GET_IN_STORE_OFFER,
      params,
      coreAppJsonHeader(JSON.stringify(params))
    );
  }

  acceptInStoreOffer(
    params: InStoreAcceptOfferRequest
  ): NetworkResponse<never> {
    return apiClient.post(
      apiUrl.ACCEPT_IN_STORE_OFFER,
      params,
      coreAppJsonHeader(JSON.stringify(params))
    );
  }

  declineInStoreOffer(params: InStoreOfferRequest): NetworkResponse<never> {
    return apiClient.post(
      apiUrl.DECLINE_IN_STORE_OFFER,
      params,
      coreAppJsonHeader(JSON.stringify(params))
    );
  }

  createCardUser(
    params: CreateCardUserRequest
  ): NetworkResponse<CreateCardUserResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_CREATE_CARD_USER,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_CREATE_CARD_USER),
        JSON.stringify(params)
      )
    );
  }

  creditCheckPermission(
    params: ICreditCheckPermissionRequest
  ): NetworkResponse<CreditCheckPermissionResponse> {
    return apiClient.post(
      apiUrl.CREDIT_CHECK_PERMISSION,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.CREDIT_CHECK_PERMISSION),
        JSON.stringify(params)
      )
    );
  }

  acceptSignature(
    params: IAcceptSignatureRequest
  ): NetworkResponse<AcceptSignatureResponse> {
    return apiClient.post(
      apiUrl.ACCEPT_SIGNATURE,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.ACCEPT_SIGNATURE),
        JSON.stringify(params)
      )
    );
  }

  reportCardTransaction(
    params: IReportCardTransactionRequest
  ): NetworkResponse<ReportCardTransactionResponse> {
    return apiClient.post(
      apiUrl.REPORT_CARD_TRANSACTION,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.REPORT_CARD_TRANSACTION),
        JSON.stringify(params)
      )
    );
  }

  fetchCardTransactionReport(
    params: IFetchCardTransactionReportRequest
  ): NetworkResponse<FetchTransactionReportResponse> {
    return apiClient.post(
      apiUrl.FETCH_CARD_TRANSACTION_DISPUTES,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.FETCH_CARD_TRANSACTION_DISPUTES),
        JSON.stringify(params)
      )
    );
  }

  triggerCredolabReportsDownload(
    applicationId: string,
    caller: string
  ): NetworkResponse<TriggerCredolabDownloadResponse> {
    return apiClient.get(
      apiUrl.TRIGGER_CREDOLAB_REPORTS_FETCH +
        `?applicationId=${applicationId}&caller=${caller}`
    );
  }

  fetchConsumerSettings(
    params: IFetchConsumerSettingsRequest
  ): NetworkResponse<SettingsResponse> {
    return apiClient.post(
      apiUrl.CONSUMER_SETTINGS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.CONSUMER_SETTINGS),
        JSON.stringify(params)
      )
    );
  }

  getShipmentLogs(
    params: IFetchShipmentLogs
  ): NetworkResponse<ShipmentLogsResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_GET_SHIPMENT_LOGS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_GET_SHIPMENT_LOGS),
        JSON.stringify(params)
      )
    );
  }

  disableCards(
    params: DisableCardsRequest
  ): NetworkResponse<IDisableCardsResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_DASHBOARD_DISABLE_CARDS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_DASHBOARD_DISABLE_CARDS),
        JSON.stringify(params)
      )
    );
  }

  getCountryShipmentProviderConfigurations(
    params: GetCountryShipmentProviderConfigurationsRequest
  ): NetworkResponse<GetCountryShipmentProviderConfigurationsResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_DASHBOARD_GET_COUNTRY_SHIPMENT_PROVIDER_CONFIGURATIONS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(
          apiUrl.PAYJOY_DASHBOARD_GET_COUNTRY_SHIPMENT_PROVIDER_CONFIGURATIONS
        ),
        JSON.stringify(params)
      )
    );
  }

  updateCountryShipmentProviderConfigurations(
    params: UpdateCountryShipmentProviderConfigurationsRequest
  ): NetworkResponse<UpdateCountryShipmentProviderConfigurationsResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_DASHBOARD_UPDATE_COUNTRY_SHIPMENT_PROVIDER_CONFIGURATIONS,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(
          apiUrl.PAYJOY_DASHBOARD_UPDATE_COUNTRY_SHIPMENT_PROVIDER_CONFIGURATIONS
        ),
        JSON.stringify(params)
      )
    );
  }

  activateCartAddOn(
    params: ActivateCartAddOnRequest
  ): NetworkResponse<IActivateCartAddOnResponse> {
    return apiClient.post(
      apiUrl.ACTIVATE_CART_ADDON,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.ACTIVATE_CART_ADDON),
        JSON.stringify(params)
      )
    );
  }

  getCartAddOn(params: BaseRequest): NetworkResponse<IAddOnDetailsResponse> {
    return apiClient.post(
      apiUrl.GET_CART_ADDON,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.GET_CART_ADDON),
        JSON.stringify(params)
      )
    );
  }

  getShippingCoverage(
    params: BaseRequest
  ): NetworkResponse<IShippingCoverageResponse> {
    return apiClient.post(
      apiUrl.PAYJOY_CARD_SHIPPING_COVERAGE,
      params,
      jsonHeader(
        HTTP_METHOD_POST,
        urlPathName(apiUrl.PAYJOY_CARD_SHIPPING_COVERAGE),
        JSON.stringify(params)
      )
    );
  }
}

export function logFrontendMessage(
  params: IFrontendLogMessageRequest
): Promise<AxiosResponse> {
  return apiClient.post(
    apiUrl.FRONTEND_LOG_MESSAGE,
    params,
    jsonHeader(
      HTTP_METHOD_POST,
      urlPathName(apiUrl.FRONTEND_LOG_MESSAGE),
      JSON.stringify(params)
    )
  );
}
